import { MAX_COLUMNS_NUMBER_TABLE_PREVIEW } from '../utils/tableUtils'
import { isGeomField } from 'features/common/utils/isGeomField'
import { ProviderType } from 'features/connections'
import { SchemaField } from '../../data-explorer/types'
import { Subscription } from 'features/data-observatory/types'
import { DATA_TABLE_ALIAS, GEOM_TABLE_ALIAS, DATA_LIMIT } from './constants'
import { formatFqnForBigQuery } from './bigQueryQueries'
import { formatFqnForSnowflake } from './snowflakeQueries'
import { formatFqnForRedshift } from './redShiftQueries'
import { formatFqnForDatabricks } from './databricksQueries'
import { QueryBuilderNoRows } from './types'
import { formatFqnForOracle } from './oracleQueries'

export function formatTableFieldForProvider(provider: ProviderType | undefined, field: string) {
  switch (provider) {
    case ProviderType.SNOWFLAKE:
    case ProviderType.ORACLE:
      return field.toUpperCase()
    default:
      return field
  }
}

export function formatTableFqn(table: string, provider?: ProviderType) {
  switch (provider) {
    case ProviderType.CARTO:
    case ProviderType.CARTO_DW:
    case ProviderType.BIGQUERY:
      return formatFqnForBigQuery(table)
    case ProviderType.SNOWFLAKE:
      return formatFqnForSnowflake(table)
    case ProviderType.POSTGRES:
    case ProviderType.REDSHIFT:
      return formatFqnForRedshift(table)
    case ProviderType.DATABRICKS:
    case ProviderType.DATABRICKS_REST:
    case ProviderType.DATABRICKS_WAREHOUSE:
      return formatFqnForDatabricks(table)
    case ProviderType.ORACLE:
      return formatFqnForOracle(table)
    default:
      return table
  }
}

export function formatDatabaseName(database: string, provider: ProviderType) {
  switch (provider) {
    case ProviderType.CARTO:
    case ProviderType.CARTO_DW:
    case ProviderType.BIGQUERY:
    case ProviderType.DATABRICKS:
    case ProviderType.DATABRICKS_REST:
    case ProviderType.DATABRICKS_WAREHOUSE:
      return `\`${database}\``
    case ProviderType.SNOWFLAKE:
    case ProviderType.POSTGRES:
    case ProviderType.REDSHIFT:
      return `"${database}"`
    default:
      return database
  }
}

export const formatColumnName = (columnName: string, provider: ProviderType) => {
  switch (provider) {
    case ProviderType.CARTO:
    case ProviderType.CARTO_DW:
    case ProviderType.BIGQUERY:
    case ProviderType.DATABRICKS_REST:
    case ProviderType.DATABRICKS_WAREHOUSE:
      return `\`${columnName}\``
    case ProviderType.SNOWFLAKE:
    case ProviderType.POSTGRES:
    case ProviderType.REDSHIFT:
    case ProviderType.ORACLE:
      // For these providers, always quote column names to preserve case sensitivity
      // Workflow result tables may have lowercase column names that need quoted identifiers
      return `"${columnName}"`
    default:
      return columnName
  }
}

export const normalizeColumn = (field: SchemaField, provider: ProviderType) => {
  if (isGeomField(field.type)) {
    switch (provider) {
      case ProviderType.CARTO:
      case ProviderType.CARTO_DW:
      case ProviderType.BIGQUERY:
        return `LTRIM(ST_GEOMETRYTYPE(${formatColumnName(field.name, provider)}), 'ST_') AS ${formatColumnName(
          field.name,
          provider
        )}`
      case ProviderType.SNOWFLAKE:
        return `SPLIT_PART(ST_ASTEXT(${formatColumnName(field.name, provider)}), '(', 0) AS ${formatColumnName(
          field.name,
          provider
        )}`
      case ProviderType.REDSHIFT:
      case ProviderType.POSTGRES:
        return `SPLIT_PART(ST_ASTEXT(${formatColumnName(field.name, provider)}), '(', 1) AS ${formatColumnName(
          field.name,
          provider
        )}`
      case ProviderType.ORACLE:
        return `'"' || REGEXP_SUBSTR(SDO_UTIL.TO_WKTGEOMETRY(${formatColumnName(
          field.name,
          provider
        )}), '^[A-Z]+') || '"' AS ${formatColumnName(field.name, provider)}`
      case ProviderType.DATABRICKS_REST: {
        const column = formatColumnName(field.name, provider)
        return `CASE
    WHEN ${column} IS NULL THEN NULL
    WHEN ST_ISVALID(TRY_TO_GEOMETRY(${column})) THEN
      LTRIM('ST_', ST_GEOMETRYTYPE(TRY_TO_GEOMETRY(${column})))
    ELSE
      BASE64(${column})
  END AS ${column}`
      }
      case ProviderType.DATABRICKS_WAREHOUSE:
        return `LTRIM('ST_', ST_GEOMETRYTYPE(${formatColumnName(field.name, provider)})) AS ${formatColumnName(
          field.name,
          provider
        )}`
    }
  }
  return formatColumnName(field.name, provider)
}

export function getQueryForDOParams({
  isSample,
  doLocation,
  subscription,
  provider
}: {
  isSample?: boolean
  doLocation?: string
  subscription?: Subscription
  provider: ProviderType
}) {
  const isGeography = subscription?.dataset_category === 'Geography'
  const spatialIndexType = subscription?.spatial_index
  const dataSlug = subscription?.dataset_slug
  const dataTable = subscription?.dataset_table
  const geoSlug = subscription?.associated_geography_slug
  const geoTable = subscription?.associated_geography_table
  let fqn = ''
  let geoFqn = ''

  if (isSample) {
    fqn = `${doLocation}.${
      provider === ProviderType.POSTGRES && dataSlug ? `sample_${dataSlug}` : dataTable?.replace(/^sub_/, 'sample_')
    }`
  } else {
    fqn = `${doLocation}.${provider === ProviderType.POSTGRES && dataSlug ? `sub_${dataSlug}` : dataTable}`
    geoFqn = isGeography
      ? ''
      : `${doLocation}.${provider === ProviderType.POSTGRES && geoSlug ? `sub_${geoSlug}` : geoTable}`
  }

  fqn = formatTableFqn(fqn, provider)
  geoFqn = formatTableFqn(geoFqn, provider)

  return {
    fqn,
    geoFqn,
    isGeography,
    spatialIndexType,
    doLocation
  }
}

export function buildQueryForDO({
  fqn,
  geoFqn,
  isSample,
  isGeography,
  spatialIndexType,
  provider
}: {
  fqn: string
  geoFqn: string
  isSample?: boolean
  isGeography?: boolean
  spatialIndexType?: string
  provider: ProviderType
}) {
  if (isSample) {
    if (spatialIndexType) {
      return createSelectForSpatialIndexDODatasets({ fqn, spatialIndexType, provider })
    }
    return createSelectForDOSample({ fqn, provider })
  }
  if (spatialIndexType) {
    return createSelectForSpatialIndexDODatasets({ fqn, spatialIndexType, provider })
  }
  if (isGeography) {
    return createSelectForDOGeography({ fqn, provider })
  }
  return createSelectForDODataset({ fqn, geoFqn, provider })
}

function createSelectForDODataset({
  fqn,
  geoFqn,
  provider
}: Required<Pick<QueryBuilderNoRows, 'fqn' | 'geoFqn' | 'provider'>>) {
  let tableName, geometryTableName
  switch (provider) {
    case ProviderType.CARTO:
    case ProviderType.CARTO_DW:
    case ProviderType.BIGQUERY:
      tableName = formatFqnForBigQuery(fqn)
      geometryTableName = formatFqnForBigQuery(geoFqn)
      break
    case ProviderType.SNOWFLAKE:
      tableName = formatFqnForSnowflake(fqn)
      geometryTableName = formatFqnForSnowflake(geoFqn)
      break
    case ProviderType.POSTGRES:
    case ProviderType.REDSHIFT:
      tableName = formatFqnForRedshift(fqn)
      geometryTableName = formatFqnForRedshift(geoFqn)
      break
    default:
      throw new Error('Only CARTO DW, BigQuery, Snowflake, Redshift or Postgres are supported')
  }

  return `
SELECT ${DATA_TABLE_ALIAS}.*, ${GEOM_TABLE_ALIAS}.geom
  FROM ${tableName} ${DATA_TABLE_ALIAS}
  INNER JOIN ${geometryTableName} ${GEOM_TABLE_ALIAS} ON ${DATA_TABLE_ALIAS}.geoid=${GEOM_TABLE_ALIAS}.geoid
`
}

function createSelectForSpatialIndexDODatasets({
  fqn,
  spatialIndexType,
  provider
}: Required<Pick<QueryBuilderNoRows, 'fqn' | 'spatialIndexType' | 'provider'>>) {
  switch (provider) {
    case ProviderType.CARTO:
    case ProviderType.CARTO_DW:
    case ProviderType.BIGQUERY:
      return `SELECT geoid AS ${spatialIndexType}, * EXCEPT(geoid) FROM ${formatFqnForBigQuery(fqn)}`
    case ProviderType.SNOWFLAKE:
      return `SELECT geoid AS ${spatialIndexType}, * EXCLUDE geoid FROM ${formatFqnForSnowflake(fqn)}`
    case ProviderType.REDSHIFT:
    case ProviderType.POSTGRES:
      // NOTE: There is no way to exclude columns from the query in RS and PG
      return `SELECT geoid AS ${spatialIndexType}, * FROM ${formatFqnForRedshift(fqn)}`
    default:
      throw new Error('Only CARTO DW, BigQuery, Snowflake, Redshift or Postgres are supported')
  }
}

function createSelectForDOGeography({ fqn, provider }: Required<Pick<QueryBuilderNoRows, 'fqn' | 'provider'>>) {
  const columns = '*'
  switch (provider) {
    case ProviderType.CARTO:
    case ProviderType.CARTO_DW:
    case ProviderType.BIGQUERY:
      return `SELECT ${columns} FROM ${formatFqnForBigQuery(fqn)}`
    case ProviderType.SNOWFLAKE:
      return `SELECT ${columns} FROM ${formatFqnForSnowflake(fqn)}`
    case ProviderType.REDSHIFT:
    case ProviderType.POSTGRES:
      return `SELECT ${columns} FROM ${formatFqnForRedshift(fqn)}`
    default:
      throw new Error('Unknown provider to build the query')
  }
}

function createSelectForDOSample({ fqn, provider }: Required<Pick<QueryBuilderNoRows, 'fqn' | 'provider'>>) {
  const columns = '*'
  switch (provider) {
    case ProviderType.CARTO:
    case ProviderType.CARTO_DW:
    case ProviderType.BIGQUERY:
      return `SELECT ${columns} FROM ${formatFqnForBigQuery(fqn)}`
    case ProviderType.SNOWFLAKE:
      return `SELECT ${columns} FROM ${formatFqnForSnowflake(fqn)}`
    case ProviderType.REDSHIFT:
    case ProviderType.POSTGRES:
      return `SELECT ${columns} FROM ${formatFqnForRedshift(fqn)}`
    default:
      throw new Error('Unknown provider to build the query')
  }
}

export function buildQueryForDataTable({
  table,
  schema,
  provider,
  limit = DATA_LIMIT,
  offset = 0
}: {
  table: string
  schema: SchemaField[]
  provider: ProviderType
  limit?: number
  offset?: number
}) {
  const geomFields = schema.filter((field) => isGeomField(field.type))
  const filteredSchema = geomFields.length
    ? [...geomFields, ...schema.filter((field) => !isGeomField(field.type))]
    : schema

  const columns = filteredSchema
    .slice(0, MAX_COLUMNS_NUMBER_TABLE_PREVIEW)
    .map((field) => normalizeColumn(field, provider))
    .join(',')

  if (provider === ProviderType.ORACLE) {
    return `SELECT ${columns || '*'} FROM ${formatTableFqn(table, provider)} OFFSET ${
      offset * limit
    } ROWS FETCH FIRST ${limit} ROWS ONLY`
  }

  return `SELECT ${columns || '*'} FROM ${formatTableFqn(table, provider)} LIMIT ${limit} OFFSET ${limit * offset}`
}

export function buildQueryForDeleteTable({ provider, table }: { provider: ProviderType; table: string }): string {
  return `DROP TABLE ${formatTableFqn(table, provider)}`
}

export function buildQueryForSubscriptionDates(fqn: string) {
  return `SELECT DISTINCT (do_data.do_date) FROM ${fqn} do_data ORDER BY do_data.do_date DESC`
}

export function buildQueryForSubscriptionExport({
  uri,
  fqn,
  geoFqn,
  datesFilters
}: {
  uri: string
  fqn: string
  geoFqn?: string
  datesFilters?: string[]
}) {
  let query = 'EXPORT DATA'
  query += `\n\tOPTIONS (\n\t\turi = '${uri}',\n\t\tformat = 'CSV',\n\t\toverwrite = true,\n\t\theader = true,\n\t\tfield_delimiter = ',')`
  query += '\nAS ('
  if (geoFqn) {
    query += '\nSELECT do_geo.geom, do_data.*'
  } else {
    query += '\nSELECT *'
  }
  query += `\nFROM \`${fqn}\` do_data`
  if (geoFqn) {
    query += `\nINNER JOIN \`${geoFqn}\` do_geo`
    query += '\nON do_geo.geoid = do_data.geoid'
  }
  if (datesFilters && datesFilters.length > 0) {
    const datesFormatted = datesFilters.map((date) => `"${date}"`).join(',')
    query += `\nWHERE do_data.do_date IN (${datesFormatted})`
  }
  query += '\n)'
  return query
}
